import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { authStore, logout, refreshSession } from './lib/api.ts';
import { clearCache } from './lib/query.ts';
import { Link, goBack, useLocation } from './lib/router.tsx';
import { BrandMark, Icon, Loading, Toasts, cx } from './components/ui.tsx';
import { OfflineBar, useReconnect } from './components/OfflineBar.tsx';
import { PageGuard } from './components/PageGuard.tsx';
import { PullToRefresh } from './components/PullToRefresh.tsx';
import { TransactionModal } from './features/transactions/public.ts';
import { LoginPage } from './features/identity/public.ts';
import { PeriodPage } from './features/periods/public.ts';
import { TransactionsPage } from './features/transactions/public.ts';
import { WalletsPage } from './features/wallets/public.ts';
import { DebtsPage } from './features/debts/public.ts';
import { PlanPage } from './features/capital/public.ts';
import { WishlistPage } from './features/wishlist/public.ts';
import { ForeignPage } from './features/foreign/public.ts';
import { ReportsPage, CategoryReportPage } from './features/reports/public.ts';
import { AnalyticsPage } from './features/analytics/public.ts';
import { SettingsPage } from './features/settings/public.ts';

/**
 * Three parts of the app: the two-week period (home), money as it is (operations, accounts, debts)
 * and the long run (goals, plan, in «Другие разделы»). `tab` puts a section into the phone tab bar; `under` keeps the
 * parent tab lit on a sub-page.
 */
const nav: { to: string; label: string; icon: string; tab?: boolean; under?: string; also?: string[]; sep?: boolean; ai?: boolean }[] = [
  { to: '/', label: 'Период', icon: 'budget', tab: true, also: ['/budget'] },
  { to: '/transactions', label: 'Операции', icon: 'list', tab: true },
  { to: '/wallets', label: 'Счета', icon: 'wallet', tab: true },
  { to: '/debts', label: 'Долги', icon: 'debt', under: '/wallets' },
  { to: '/settings', label: 'Настройки', icon: 'settings', tab: true },
  { to: '/wishlist', label: 'Покупки', icon: 'cart', sep: true },
  { to: '/foreign', label: 'В валюте', icon: 'coins' },
  { to: '/reports', label: 'Отчёты', icon: 'chart' },
  // only when the server has the Codex worker configured
  { to: '/ai', label: 'Аналитика', icon: 'bulb', ai: true },
  { to: '/plan', label: 'Капитал', icon: 'plan' },
];

const pages: Record<string, () => React.ReactNode> = {
  '/': () => <PeriodPage />,
  '/budget': () => <PeriodPage />,
  '/transactions': () => <TransactionsPage />,
  '/wallets': () => <WalletsPage />,
  '/debts': () => <DebtsPage />,
  '/plan': () => <PlanPage />,
  '/wishlist': () => <WishlistPage />,
  '/foreign': () => <ForeignPage />,
  '/reports': () => <ReportsPage />,
  '/reports/category': () => <CategoryReportPage />,
  '/ai': () => <AnalyticsPage />,
  '/settings': () => <SettingsPage />,
};

export function App() {
  const auth = useSyncExternalStore(authStore.subscribe, authStore.get);
  useEffect(() => { void refreshSession(); }, []);
  useEffect(() => { if (auth.status === 'out') clearCache(); }, [auth.status]);

  if (auth.status === 'loading') return <div className="boot"><Loading label="Открываю трекер…" /></div>;
  if (auth.status === 'out') return <><LoginPage /><Toasts /></>;
  return <Shell />;
}

function Shell() {
  const { path, query } = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);
  // the operation sheet: plain, or with the phrase field for typing or dictating it
  const [adding, setAdding] = useState<false | 'form' | 'words'>(false);
  const mainRef = useRef<HTMLElement>(null);
  useEffect(() => setMoreOpen(false), [path]);
  useReconnect();
  const page = pages[path];
  const matches = (n: (typeof nav)[number]) => path === n.to || !!n.also?.includes(path) || (n.to !== '/' && path.startsWith(`${n.to}/`));
  const current = nav.find(matches);
  const tabActive = (n: (typeof nav)[number]) => matches(n) || current?.under === n.to;
  const user = authStore.get().user;
  const shown = nav.filter((n) => !n.ai || user?.ai);
  // next to the mic: dictate an operation, or ask Codex about the money (only when the worker is configured)
  const mic = user?.assistant ? (
    <button type="button" className="icon-btn icon-btn-framed" aria-label="Добавить голосом или текстом" onClick={() => setAdding('words')}>
      <Icon name="mic" />
    </button>
  ) : null;
  const ai = user?.ai ? (
    <Link to="/ai" className="icon-btn icon-btn-framed" aria-label="Аналитика" aria-current={path === '/ai' ? 'page' : undefined}>
      <Icon name="bulb" />
    </Link>
  ) : null;
  // a page outside the tab bar, or a filtered view, gets a back button on phones
  const canGoBack = !current?.tab || query.size > 0;

  return (
    <div className="shell">
      <aside className="sidebar">
        <div className="brand">
          <BrandMark />
          <span>Трекер трат</span>
        </div>
        <div className="sidebar-actions">
          <button type="button" className="btn btn-primary sidebar-add" onClick={() => setAdding('form')}><Icon name="plus" /> Операция</button>
          {mic}
          {ai}
        </div>
        <nav aria-label="Разделы">
          {shown.map((n) => (
            <Link key={n.to} to={n.to} className={cx('nav-item', n.sep && 'nav-sep', n.under && 'nav-sub', matches(n) && 'active')}
              aria-current={matches(n) ? 'page' : undefined}>
              <Icon name={n.icon} /> {n.label}
            </Link>
          ))}
        </nav>
        <div className="sidebar-foot">
          <span className="muted small">{user?.email}</span>
          <button type="button" className="nav-item" onClick={() => void logout()}><Icon name="logout" /> Выйти</button>
        </div>
      </aside>

      <main className="main" id="main" ref={mainRef}>
        <header className="app-header">
          <span className="app-header-title">
            {canGoBack ? <button type="button" className="icon-btn icon-btn-framed" aria-label="Назад" onClick={goBack}><Icon name="left" /></button> : null}
            <h1>{current?.label ?? 'Трекер трат'}</h1>
          </span>
          <span className="app-header-actions">
            {mic}
            {ai}
            <button type="button" className="icon-btn icon-btn-framed" aria-label="Другие разделы" aria-expanded={moreOpen} onClick={() => setMoreOpen((v) => !v)}>
              <Icon name="more" />
            </button>
          </span>
        </header>
        <OfflineBar />
        <div className="page-host" key={path}>
          <PageGuard>{page ? page() : <div className="empty"><p>Такой страницы нет.</p><Link to="/">К периоду</Link></div>}</PageGuard>
        </div>
      </main>
      <PullToRefresh target={mainRef} />

      <nav className="tabbar" aria-label="Разделы">
        {nav.filter((n) => n.tab).slice(0, 2).map((n) => <Tab key={n.to} n={n} active={tabActive(n)} />)}
        <span className="tab-add-cell">
          <button type="button" className="tab-add" aria-label="Добавить операцию" onClick={() => setAdding('form')}><Icon name="plus" size={26} /></button>
        </span>
        {nav.filter((n) => n.tab).slice(2).map((n) => <Tab key={n.to} n={n} active={tabActive(n)} />)}
      </nav>
      {moreOpen ? (
        <div className="more-sheet" onClick={() => setMoreOpen(false)}>
          <div className="more-panel" onClick={(e) => e.stopPropagation()}>
            {shown.filter((n) => !n.tab).map((n) => (
              <Link key={n.to} to={n.to} className={cx('nav-item', matches(n) && 'active')}><Icon name={n.icon} /> {n.label}</Link>
            ))}
            <button type="button" className="nav-item" onClick={() => void logout()}><Icon name="logout" /> Выйти</button>
          </div>
        </div>
      ) : null}
      {/* opened from a wallet's operations, a new operation starts in that wallet */}
      {adding ? <TransactionModal words={adding === 'words'} defaults={path === '/transactions' && query.get('wallet') ? { walletId: Number(query.get('wallet')) } : undefined} onClose={() => setAdding(false)} /> : null}
      <Toasts />
    </div>
  );
}

function Tab({ n, active }: { n: (typeof nav)[number]; active: boolean }) {
  return (
    <Link to={n.to} className={cx('tab', active && 'active')} aria-current={active ? 'page' : undefined}>
      <Icon name={n.icon} size={22} /><span>{n.label}</span>
    </Link>
  );
}
